// src/components/kuestion/campus/OrgWizard.jsx
//
// First run: name the organisation, confirm the (server-generated) prefix,
// then add students. The prefix preview on step 2 mirrors the backend rule
// but the real value comes back from POST /campus/org and is shown on step 3.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useAuth } from '../../../context/AuthContext';
import { useCampus } from './CampusContext';
import { ThemeToggle } from '../shared/ThemeContext';
import AppNoticeboard from '../shared/AppNoticeboard';
import { Mark, Alert } from '../shared/Bits';
import { InfoIcon, CheckIcon, UploadIcon, AddStudentIcon } from '../shared/Icons';
import { previewPrefix } from '../shared/password';
import { deviceZone, zoneLabelFor } from '../shared/display';
import { kq } from '../../../config/routes';

export default function OrgWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { api, setOrg, reloadOrg } = useCampus();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const preview = previewPrefix(name);
  const shown = name.trim() || 'Your organisation';

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await api.postJson(API_ENDPOINTS.CAMPUS_ORG, { display_name: name.trim() });
      setCreated(data);
      setStep(3);
    } catch (e) {
      // Someone else's tab already created it — pick it up rather than fail.
      if (e.reason === 'org_already_exists') { await reloadOrg(); return; }
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Entering the app commits the org into context; the gate then swaps the
  // wizard for the shell.
  const enter = (to) => {
    setOrg(created);
    navigate(to, { replace: true });
  };

  const prefix = created?.prefix || preview;

  return (
    <div className="kqc kqc-wizard">
      <div className="kqc-topbar">
        <span className="kqc-topbar__mark"><Mark /></span>
        <span className="kqc-topbar__brand">Kuestion Campus</span>
        <span className="kqc-spacer" />
        <AppNoticeboard />
        <ThemeToggle />
        <span className="tag tag-neutral">Signed in as {user?.email || user?.username}</span>
      </div>

      <div className="kqc-wizard__body">
        <div className="kqc-wizard__inner">
          <div className="kqc-steps">
            {[['Step 1', 'Name it'], ['Step 2', 'Confirm the prefix'], ['Step 3', 'Add students']].map(([s, t], i) => (
              <div key={s} className={`kqc-step${step === i + 1 ? ' is-current' : step > i + 1 ? ' is-done' : ''}`}>
                <small>{s}</small><strong>{t}</strong>
              </div>
            ))}
          </div>

          {step === 1 && (
            <>
              <div className="kqc-kicker" style={{ marginBottom: 14 }}>One-time setup</div>
              <h1 className="kqc-h1--big" style={{ marginBottom: 16, maxWidth: '24ch' }}>Name your organisation</h1>
              <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 32, maxWidth: '56ch' }}>This is the name your students and reports will show. You can change it whenever you like.</p>

              <div className="field" style={{ maxWidth: 520, marginBottom: 28 }}>
                <label htmlFor="org-name">Organisation display name</label>
                <input id="org-name" className="input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Rasters School" autoFocus maxLength={80} />
              </div>

              <div className="kqc-box kqc-box--surface" style={{ maxWidth: 620, marginBottom: 28 }}>
                <div className="kqc-box__body kqc-box__body--pad">
                  <div className="kqc-row" style={{ marginBottom: 14 }}>
                    <span style={{ display: 'flex', width: 26, height: 26, color: 'var(--color-accent)' }}><InfoIcon /></span>
                    <span className="kqc-mono" style={{ fontSize: 15 }}>Two names, two different jobs</span>
                  </div>
                  <div className="kqc-twonames">
                    <div>
                      <strong>Display name — yours to change</strong>
                      <em>Appears on screens and reports. Rename it any time.</em>
                    </div>
                    <div>
                      <strong className="kqc-accent-ink">Login prefix — permanent</strong>
                      <em>We generate six characters from the name. It becomes part of every student login, so it can never change.</em>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seeded, not prompted (Preferences contract §5.1). Until
                  /api/org/settings ships there is nowhere to persist it, so
                  this is a statement, not a form field (client note v5.2 §2). */}
              <div className="kqc-small" style={{ maxWidth: 620, marginBottom: 24, color: 'var(--color-neutral-800)' }}>
                <strong style={{ fontFamily: 'var(--font-heading)' }}>Organisation timezone:</strong> {deviceZone() ? zoneLabelFor(deviceZone()) : 'UTC'}, detected from your browser. Exam windows are shown to each student in their own zone, named. Until organisation settings ship, the organisation has no default zone of its own and each student sees their own; it is never filled in from your browser behind your back.
              </div>

              <div className="kqc-row kqc-row--gap">
                <button type="button" className="btn btn-primary" style={{ minHeight: 48 }} disabled={!name.trim()} onClick={() => setStep(2)}>Continue</button>
                <span className="kqc-small kqc-muted">Nothing is created until you confirm on the next step.</span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="kqc-kicker" style={{ marginBottom: 14 }}>One-time setup · Step 2 of 3</div>
              <h1 className="kqc-h1--big" style={{ fontSize: 'clamp(28px,4.4vw,48px)', marginBottom: 16, maxWidth: '26ch' }}>
                Your login prefix will be <span className="kqc-accent">{preview}</span>
              </h1>
              <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 28 }}>
                Generated from &ldquo;{shown}&rdquo;. Every student login in your organisation will start with it. If another organisation already holds it, the server adjusts the last character.
              </p>

              <div className="kqc-box" style={{ maxWidth: 620, marginBottom: 24 }}>
                <div className="kqc-box__head">What a student will type</div>
                <div className="kqc-box__body kqc-box__body--surface" style={{ padding: 'clamp(18px,3vw,30px) 16px' }}>
                  <div className="kqc-prefixdemo">
                    <span className="p">{preview}</span><span className="d">-</span><span>A7K2M9</span>
                  </div>
                  <div>
                    <span>Your prefix · fixed</span>
                    <span>Student code · you choose</span>
                  </div>
                </div>
              </div>

              <div className="kqc-box kqc-box--danger" style={{ maxWidth: 620, marginBottom: 28 }}>
                <div className="kqc-box__body">
                  <div className="kqc-mono is-danger" style={{ fontSize: 14, marginBottom: 6 }}>This cannot be undone</div>
                  <div className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 14 }}>Once created, the prefix is fixed for the life of the organisation. The display name stays editable.</div>
                  <label className="kqc-checkline">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                    <span>I understand that the prefix is permanent.</span>
                  </label>
                </div>
              </div>

              {error && <Alert kind="error">{error}</Alert>}

              <div className="kqc-row kqc-row--gap">
                <button type="button" className="btn btn-primary" style={{ minHeight: 48 }} disabled={!ack || busy} onClick={create}>{busy ? 'Creating…' : 'Create organisation'}</button>
                <button type="button" className="btn btn-secondary" style={{ minHeight: 48 }} disabled={busy} onClick={() => setStep(1)}>Change the name</button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <span className="kqc-bigcheck kqc-bigcheck--sm"><CheckIcon /></span>
              <h1 className="kqc-h1--big" style={{ marginBottom: 14 }}>{created?.display_name || shown} is live</h1>
              <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 30, maxWidth: '52ch' }}>
                Prefix <strong>{prefix}</strong>. Now add the people who will sit your exams.
              </p>

              <div className="kqc-tiles">
                <button type="button" className="kqc-tile" onClick={() => enter(kq('students/import'))}>
                  <span className="i"><UploadIcon /></span>
                  <span><strong>Import a CSV</strong><em>A whole class at once. Passwords generated for you.</em></span>
                </button>
                <button type="button" className="kqc-tile" onClick={() => enter(kq('students/new'))}>
                  <span className="i"><AddStudentIcon /></span>
                  <span><strong>Add one student</strong><em>Type a code, generate a password, hand it over.</em></span>
                </button>
              </div>

              <button type="button" className="btn btn-ghost" onClick={() => enter(kq())}>Skip for now — go to the dashboard &rarr;</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
